import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import { addAlert } from "../../state/system/Alerts";
import Group from "../../types/archive/Group";
import File from "../../types/files/File";
import { AlertTypes } from "../../types/system/AlertEntry";
import {
    initialiseTimetableDetails,
    reinitialiseTimetableDetails,
} from "../archive/Timetables";
import Utility from "../../foundation/Utility";
import {
    ImmediateMonitor,
    StartMonitor,
    StopMonitor,
} from "../../sockets/statusManager";
import { getSetting } from "../system/Settings";

import Datastore from "../../communication/Datastore";

import store, { IAppState } from "../../state/Store";
import Calendar from "../../types/organiser/Calendar";
import { initialiseSchoolDetails } from "../core/School";
import {
    initialiseOrganiserTemplate,
    reinitialiseOrganiserDetails,
} from "../organiser/Organisers";
import { unwrappedDispatch } from "../../foundation/Dispatching";

// Define the type for our state store entry
type ImportsState = {
    imports: File[];
    status: string;
    age?: Date;
    watching: boolean;
    error: string;
};

// Define the initial state for our state store entry
const initialState: ImportsState = {
    imports: [],
    status: "idle",
    watching: false,
    error: null,
};

// Create a slice to hold all our reducers
const importsSlice = createSlice({
    name: "imports",
    initialState: initialState,
    reducers: {
        resetImports: state => {
            state.imports = [];
            state.status = "idle";
            state.age = null;
        },
        setImports: (state, action: PayloadAction<File[]>) => {
            state.imports = action.payload.map(item => new File(item));
        },
        setWatching: (state, action: PayloadAction<boolean>) => {
            state.watching = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                return {
                    imports: [],
                    status: "idle",
                    age: rawData?.payload?.imports?.age?.toDate(),
                    watching: false,
                    error: null,
                };
            })
            .addCase(fetchImports.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchImports.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.age = new Date();
                state.imports = action.payload.map(item => new File(item));
            })
            .addCase(fetchImports.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { setImports, resetImports, setWatching } = importsSlice.actions;

// This is a thunk, they are used to load the state
export const fetchImports = createAsyncThunk(
    "imports/fetchImports",
    async (): Promise<File[]> => {
        return await Datastore.getType<File[]>("api/SystemIO/Files/Publishing");
    },
    {
        condition: (_, { getState }) => {
            const { imports } = (getState() as IAppState).db;
            return (
                imports.status !== "loading" &&
                (!imports.age || imports.age.addSeconds(30) < new Date())
            );
        },
    },
);
export const initialiseImports = createAsyncThunk(
    "imports/initialiseImports",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(fetchImports());
        if (!store.getState().db.imports.watching) {
            WatchImports();
            store.dispatch(setWatching(true));
        }
    },
);
export const reinitialiseImports = createAsyncThunk(
    "imports/reinitialiseImports",
    async (_, thunkAPI) => {
        thunkAPI.dispatch(resetImports());
        await thunkAPI.dispatch(fetchImports());
        if (!store.getState().db.imports.watching) {
            WatchImports();
            store.dispatch(setWatching(true));
        }
    },
);
export const importDOFiles = async (
    campusId: number,
    year: number,
    files: ImportParams[],
) => {
    await Promise.all([
        unwrappedDispatch(initialiseSchoolDetails()),
        initialiseOrganiserTemplate(),
    ]);
    const { groups, school, campus, organisers } = store.getState().db;

    const doFolder = await getSetting(Number, "dailyOrganiserFolder");
    const useCampus = campus.campuses.find(c => c.id === campusId);
    const useYear = useCampus.terms.find(t => t.id === year);
    const startDate = useYear.ranges.first().startDate.toDate();
    const endDate = useYear.ranges.last().endDate.toDate();
    const group = groups.groups.find(
        g => g.campusID === campusId && g.termDatesID === year,
    );

    const newName =
        startDate.getFullYear() === endDate.getFullYear()
            ? `${useCampus.name} - ${startDate.getFullYear()}`
            : `${
                  useCampus.name
              } - ${startDate.getFullYear()}/${endDate.getFullYear()}`;
    await Promise.all(
        files.map(async file => {
            const published = await Datastore.postType<File>(
                `api/SystemIO/DO/${file.File.id}/Copy/${doFolder}/Name`,
                newName,
            );
            const linked = await Datastore.getType<Calendar[]>(
                `api/Organisers/DO/${published.id}`,
            );

            const calendar = linked.length ? linked[0] : organisers.template;

            calendar.name = newName;
            calendar.campusID = campusId;
            calendar.termDatesID = year;
            calendar.archiveGroupID = group.id;
            calendar.schoolID = school.school.id;
            calendar.dofid = published.id;
            calendar.firstTimetableDayOrderNo = 0;

            if (calendar.id === 0)
                await Datastore.post("api/Organisers", [calendar]);
            else await Datastore.put("api/Organisers", [calendar]);
        }),
    );

    await reinitialiseOrganiserDetails();
    ImmediateImports();
};
export const importRawFiles = async (files: File[]) => {
    const publishedFolder = await getSetting(Number, "dailyOrganiserFolder");

    await Datastore.postType<File>(
        `api/SystemIO/Folders/${publishedFolder}/Files/Copy`,
        files,
    );

    // await reinitialiseCalendarDetails();
    ImmediateImports();
};
export interface ImportParams {
    File: File;
    StartDate: Date;
    OverwriteName?: string;
}
const checkOverlaps = async (group: Group, processing: ImportParams[]) => {
    const tests = processing
        .filter(item => !Utility.notNullOrEmpty(item.OverwriteName))
        .map(item => item.StartDate);
    if (tests.length === 0) return [];

    const result = await Datastore.post(
        `/api/Timetables/Published/TimetableGroups/${group.id}/CheckOverlap`,
        tests,
    );
    return result.split("\n").filter(line => line.trim() !== "");
};
const showCancellation = (messages: string[]) => {
    addAlert({
        type: AlertTypes.Alert,
        icon: "error",
        styleColor: "red",
        title: "Import Error",
        text: `The following issues prevented the publish:<ul><li>${messages.join(
            "</li><li>",
        )}</li></ul>Please retry your publish later.`,
    });
};
export const importTDFiles = async (
    campusId: number,
    year: number,
    files: ImportParams[],
) => {
    await initialiseTimetableDetails();
    const { groups, timetable } = store.getState().db;

    const group = groups.groups.find(
        g => g.campusID === campusId && g.termDatesID === year,
    );

    const clashedTimetables = [];
    const clashes = await checkOverlaps(group, files);
    if (clashes.length > 0) {
        const blocks = clashes.filter(
            clash => !clash.startsWith("There is already a"),
        );

        if (blocks.length > 0) {
            showCancellation(blocks);
            return;
        }

        if (clashes.length > 0) {
            const result = await addAlert({
                type: AlertTypes.Confirm,
                icon: "warning",
                styleColor: "yellow",
                title: "Import Error",
                text: `The following issues were detected for group "${
                    group.name
                }":<ul><li>${clashes.join(
                    "</li><li>",
                )}</li></ul>Would you like to overwrite these files?<br>`,
                okText: "Overwrite",
                cancelText: "Cancel Import",
            });

            if (!result.response) return;
            else
                for (const file of files) {
                    const clash = timetable.timetables.find(
                        t =>
                            t.groupId === group.id &&
                            t.startDate.equalsDate(file.StartDate),
                    );
                    if (clash) {
                        file.OverwriteName = clash.name;
                        clashedTimetables.push(clash);
                    }
                }
        }
    }

    const outstandingIssues =
        clashes.length > 0 ? await checkOverlaps(group, files) : [];
    if (outstandingIssues.length > 0) {
        showCancellation(outstandingIssues);
        return;
    }

    await Promise.all(
        files.map(file =>
            Datastore.post(
                file.OverwriteName
                    ? `api/Timetables/Published/TimetableGroups/${group.id}/TD/${file.OverwriteName}/Publish/TD/${file.File.id}`
                    : `api/Timetables/Published/TimetableGroups/${group.id}/Publish/TD/${file.File.id}`,
                {
                    startDate: file.StartDate,
                    comment: "",
                },
            ),
        ),
    );

    await reinitialiseTimetableDetails();
    ImmediateImports();
};

// We have to export the reducer as this is added to the state store
export default importsSlice.reducer;

const watchType: string = "importstatus";

export function WatchImports() {
    StartMonitor(watchType, 0, 10000, true, function (data: File[]) {
        store.dispatch(setImports(data));
    });
}

export function ImmediateImports() {
    ImmediateMonitor(watchType, 0);
}

export function StopWatchImports() {
    StopMonitor(watchType, 0);
}
